import { memo, useEffect, useMemo, useRef, useState } from "react";
import { BookOpenText, Download, FileText, Library, Loader2, Search, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { attachGutenberg, removeEbook, searchGutenberg, uploadEbook } from "@/lib/transcript";
import { SIZES, SIZE_CLASS, setReadAlongPref, stepSize, useReadAlongPrefs } from "@/lib/readAlongPrefs";

/* One paragraph of the attached text. Only the active one re-renders while audio plays. */
const Para = memo(({ seg, state, onSeek, idx }) => (
  <p data-seg={idx} data-state={state} role="button" tabIndex={-1} onClick={() => onSeek(seg.s)}
    className={`cursor-pointer rounded-md transition-colors duration-300 ${state === "active" ? "-mx-3 bg-white/[0.07] px-3 py-1 text-white shadow-[inset_3px_0_0_var(--tint)]" : state === "read" ? "text-white/60" : "text-white/35 hover:text-white/60"}`}>
    {seg.t}
  </p>
));

/* Read-along text for the playing chapter, paced by word count across the chapter (estimated, no AI). Tap a paragraph to jump there. */
export const ReadAlong = ({ tr, time, onSeek, className = "" }) => {
  const box = useRef(null);
  const manualUntil = useRef(0);
  const prefs = useReadAlongPrefs();
  const segs = useMemo(() => tr.segments || [], [tr.segments]);
  const active = useMemo(() => {
    let i = -1;
    for (let k = 0; k < segs.length; k++) { if (segs[k].s <= time + 0.1) i = k; else break; }
    return i;
  }, [segs, time]);
  useEffect(() => {
    if (active < 0 || !box.current || Date.now() < manualUntil.current) return;
    const el = box.current.querySelector(`[data-seg="${active}"]`);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [active]);
  const manual = () => { manualUntil.current = Date.now() + 4000; };
  return (
    <div className={`flex min-h-0 flex-col ${className}`} data-testid="read-along" data-status={tr.status}>
      {(tr.status === "loading" || tr.status === "idle") && <p className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 size={14} className="animate-spin" />Looking for the book's text…</p>}
      {tr.status === "none" && (
        <div data-testid="read-along-cta" className="rounded-xl border border-white/10 bg-white/5 p-5">
          <p className="flex items-center gap-2 font-heading text-lg text-white"><BookOpenText size={18} />No text attached yet</p>
          <p className="mt-1 text-sm text-zinc-400">{tr.can_edit ? "Open this book's page and attach its EPUB/TXT — or fetch it from Project Gutenberg — to read along while you listen. Free, no AI involved." : "Ask the library owner to attach the book's text to read along."}</p>
        </div>
      )}
      {tr.status === "unmatched" && <p data-testid="read-along-unmatched" className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-300">Couldn't match this chapter to a section of the attached text. Chapter names like "Chapter 3" in both the audio and the book help.</p>}
      {tr.status === "error" && <p data-testid="read-along-error" className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-100">{tr.error || "Read-along isn't available right now."}</p>}
      {segs.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-1 text-[11px] text-zinc-500" data-testid="read-along-toolbar">
            <span data-testid="read-along-estimated" title="Paragraphs are paced by their length across the chapter — the highlight is approximate.">{tr.section_titles?.[0] ? `${tr.section_titles[0]} · ` : ""}paced by length</span>
            <span className="ml-auto flex items-center gap-1" data-testid="read-along-font" data-size={prefs.size} data-font={prefs.font}>
              <button type="button" onClick={() => stepSize(-1)} disabled={prefs.size === SIZES[0]} data-testid="read-along-font-smaller" title="Smaller text" aria-label="Smaller text" className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"><span className="text-[11px] font-semibold">A</span></button>
              <button type="button" onClick={() => stepSize(1)} disabled={prefs.size === SIZES[SIZES.length - 1]} data-testid="read-along-font-larger" title="Larger text" aria-label="Larger text" className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"><span className="text-[15px] font-semibold">A</span></button>
              <button type="button" onClick={() => setReadAlongPref({ font: prefs.font === "serif" ? "sans" : "serif" })} data-testid="read-along-font-family" title={prefs.font === "serif" ? "Switch to sans-serif" : "Switch to serif"} className="ml-1 rounded-full border border-white/15 px-2.5 py-1 text-zinc-300 transition-colors hover:border-white/40 hover:text-white">
                <span className={prefs.font === "serif" ? "font-reading" : "font-heading"}>{prefs.font === "serif" ? "Serif" : "Sans"}</span>
              </button>
            </span>
          </div>
          <div ref={box} onWheel={manual} onTouchMove={manual} className="lyrics-scroll mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-3 [mask-image:linear-gradient(180deg,transparent,black_8%,black_90%,transparent)]" data-testid="read-along-text" data-active-seg={active}>
            <div className={`space-y-5 py-[18%] ${prefs.font === "serif" ? "font-reading" : "font-heading"} ${SIZE_CLASS[prefs.size] || SIZE_CLASS.md}`}>
              {segs.map((sg, i) => <Para key={i} idx={i} seg={sg} state={i === active ? "active" : i < active ? "read" : "future"} onSeek={onSeek} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* Search Project Gutenberg (public domain) for this book and attach the text in one tap. */
const GutenbergFinder = ({ albumId, onAttached }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attaching, setAttaching] = useState(null);
  const search = async (query) => {
    setBusy(true);
    try { const d = await searchGutenberg(albumId, query); setRes(d.results); if (!q) setQ(d.query); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const openIt = () => { setOpen(true); if (res === null) search(""); };
  const attach = async (b) => {
    setAttaching(b.id);
    try { const eb = await attachGutenberg(albumId, b.id, b.title); toast.success(`Attached "${eb.filename}"`); setOpen(false); onAttached?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setAttaching(null); }
  };
  if (!open) return <button onClick={openIt} data-testid="read-along-gutenberg" className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-[11px] font-medium hover:border-zinc-900"><Library size={12} />Find on Project Gutenberg</button>;
  return (
    <div className="w-full rounded-lg border border-zinc-200 bg-white p-3" data-testid="read-along-gutenberg-panel">
      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); search(q); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title and author" data-testid="read-along-gutenberg-query" className="h-8 min-w-0 flex-1 rounded-md border border-zinc-300 bg-white px-2 text-xs outline-none focus:border-zinc-900" />
        <button type="submit" disabled={busy} data-testid="read-along-gutenberg-search" className="flex h-8 items-center gap-1 rounded-full bg-zinc-900 px-3 text-[11px] font-medium text-white disabled:opacity-50">{busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}Search</button>
        <button type="button" onClick={() => setOpen(false)} data-testid="read-along-gutenberg-close" className="text-zinc-400 hover:text-zinc-900" aria-label="Close"><X size={14} /></button>
      </form>
      {res && !res.length && !busy && <p className="mt-2 text-[11px] text-zinc-500" data-testid="read-along-gutenberg-empty">Nothing on Project Gutenberg matches — it only has public-domain books (mostly pre-1929). Try a shorter title.</p>}
      {res?.length > 0 && (
        <ul className="mt-2 divide-y divide-zinc-100" data-testid="read-along-gutenberg-results">
          {res.map((b) => (
            <li key={b.id} className="flex items-center gap-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-zinc-900">{b.title}</span>
                <span className="block truncate text-[11px] text-zinc-500">{b.authors.join(", ") || "Unknown author"} · #{b.id}</span>
              </span>
              <button onClick={() => attach(b)} disabled={attaching !== null} data-testid={`read-along-gutenberg-attach-${b.id}`} className="flex h-7 items-center gap-1 rounded-full border border-zinc-300 px-2.5 text-[11px] font-medium hover:border-zinc-900 disabled:opacity-50">
                {attaching === b.id ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}Attach
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* Light-theme card for the book page: the attached text and how many chapters it covers. */
export const ReadAlongCard = ({ sum, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  if (!sum) return null;
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try { const eb = await uploadEbook(sum.album_id, f); toast.success(`Text attached · ${eb.chapter_count} sections, ${eb.word_count.toLocaleString()} words`); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setUploading(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the attached text? Read-along will be unavailable for this book.")) return;
    try { await removeEbook(sum.album_id); toast.success("Text removed"); onChanged?.(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <section className="mt-10 rounded-xl border border-zinc-200 bg-white/70 p-4" data-testid="read-along-card">
      <div className="flex items-start gap-3">
        <BookOpenText size={18} className="mt-0.5 shrink-0 text-zinc-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-900">Read along</p>
          <p className="text-xs text-zinc-500" data-testid="read-along-card-status">
            {sum.protected ? "Copy-protected files can't be played, so there's nothing to read along to."
              : !sum.ebook ? "Attach the book's text (EPUB/TXT, or fetch it from Project Gutenberg) and Now Playing shows it paragraph by paragraph while you listen. Free — no AI, no balance used."
                : sum.with_text === sum.total ? "Every chapter has text — open Now Playing and tap the book icon." : `${sum.with_text} of ${sum.total} chapters matched a section of the text.`}
          </p>
          {!sum.protected && (
            <div className="mt-3 flex items-start gap-3" data-testid="read-along-ebook">
              <FileText size={16} className="mt-0.5 shrink-0 text-zinc-500" />
              <div className="min-w-0 flex-1">
                {sum.ebook ? <p className="truncate text-[11px] text-zinc-600" data-testid="read-along-ebook-meta" title={sum.ebook.filename}>{sum.ebook.filename} · {sum.ebook.chapter_count} sections · {(sum.ebook.word_count || 0).toLocaleString()} words</p>
                  : <p className="text-[11px] text-zinc-500">No text attached.</p>}
                {sum.can_edit && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input ref={fileRef} type="file" accept=".epub,.txt,.md,application/epub+zip,text/plain" className="hidden" onChange={onFile} data-testid="read-along-ebook-input" />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="read-along-ebook-upload" className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-[11px] font-medium hover:border-zinc-900 disabled:opacity-50">
                      {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}{sum.ebook ? "Replace" : "Attach EPUB / TXT"}
                    </button>
                    {!sum.ebook && <GutenbergFinder albumId={sum.album_id} onAttached={onChanged} />}
                    {sum.ebook && <button onClick={remove} data-testid="read-along-ebook-remove" className="flex h-8 items-center gap-1 rounded-full px-2 text-[11px] text-zinc-500 hover:text-rose-600"><Trash2 size={12} />Remove</button>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
